import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { ImageContainer } from 'ui/layout/Containers';
import { useAppSelector } from 'app/hooks';
import { initAnalytics } from 'features/consent/analytics';
import { Button } from 'ui/atoms/buttons';


const Main = styled.main`
  padding: 40px;

  section {
    margin-bottom: 40px;
    margin-top: 140px;

    &:first-child {
      margin-top: 0;
    }

    h2 {
      font-size: 28px;
      margin-bottom: 10px;
    }

    p {
      margin-bottom: 8px;
    }

    ul {
      list-style: disc;
      padding-left: 20px;
    }

    a {
      color: #333;
    }
  }
`;

const SectionContainer = styled.section<{ align?: 'left' | 'center' }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ align }) => (align === 'left' ? 'flex-start' : 'center')};
  margin-bottom: 40px;

  .image {
    width: 100%;
    max-width: 400px;
    margin-bottom: 24px;
  }

  h3 {
    font-size: 24px;
    margin-bottom: 16px;
    text-align: ${({ align }) => align};
  }

  p {
    font-size: 18px;
    line-height: 1.5;
    margin-bottom: 16px;
    max-width: 600px;
    text-align: 'justify';
    
  }
`;

const TextParagraph = styled.p`
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 16px;
  max-width: 800px;
  text-align: justify;
`;

const ImageWrapper = styled.div<{ imageAlign?: 'left' | 'right' }>`
  display: flex;
  justify-content: ${({ imageAlign }) => (imageAlign === 'left' ? 'flex-start' : 'flex-end')};
  margin: 30px 20px;

  img {
    max-width: 800px;
  }
`;


const HeroSection = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 80vh;
  text-align: center;

  opacity: 0;
  animation: fadeIn 2s forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  h1 {
    font-size: 32px;
    margin-bottom: 16px;
  }

  p {
    font-size: 18px;
    margin-bottom: 24px;
  }
`;



export const HomePage = () => {
  const { t } = useTranslation('homepage');

  return (
    <Main>
        <HeroSection>
          <h1>{t('enjoyUsage')}</h1>
          <p>{t('tagline')}</p>
          <Link className="dropdown-item" to='/sign-up/'>
            <Button>{t('getStarted')}</Button>
          </Link>
        </HeroSection>

        <section id="about">
          <SectionContainer>
            <h2>{t('whatIsSimpleWallet')}</h2>
            <ImageWrapper>
              <img src="/images/wallet-passes.png" alt={t('walletPasses')} />
            </ImageWrapper>
            <p>{t('walletPassesDescription')}</p>
          </SectionContainer>
        </section>

        <section id="features">
          <SectionContainer>
            <h2>{t('features')}</h2>
            <p>{t('featuresSubtitle')}</p>
            <ImageWrapper>
              <img src="/images/wallet-different-passes.png" alt={t('boardingPass')} />
            </ImageWrapper>
            <h3>{t('easyAccess')}</h3><TextParagraph>{t("easyAccessDescription")}</TextParagraph>
            <h3>{t('passTypes')}</h3><TextParagraph>{t("passTypesDescription")}</TextParagraph>
            <h3>{t('customizableDesign')}</h3><TextParagraph>{t("customizableDesignDescription")}</TextParagraph>
            <h3>{t('dynamicUpdates')}</h3><TextParagraph>{t("dynamicUpdatesDescription")}</TextParagraph>
            <h3>{t('locationTimeTriggers')}</h3><TextParagraph>{t("locationTimeTriggersDescription")}</TextParagraph>
            <ImageContainer>
              <ImageWrapper>
                <img src="/images/1.png" alt={t('boardingPass')} />
                <img src="/images/3.jpg" alt={t('boardingPass')} />
              </ImageWrapper>
            </ImageContainer>
            <h3>{t('notifications')}</h3><TextParagraph>{t("notificationsDescription")}</TextParagraph>
            <h3>{t('securePrivate')}</h3><TextParagraph>{t("securePrivateDescription")}</TextParagraph>
            <h3>{t('offlineAvailability')}</h3><TextParagraph>{t("offlineAvailabilityDescription")}</TextParagraph>
            <h3>{t('integrationApps')}</h3><TextParagraph>{t("integrationAppsDescription")}</TextParagraph>
            <h3>{t('environmentallyFriendly')}</h3><TextParagraph>{t("environmentallyFriendlyDescription")}</TextParagraph>
          </SectionContainer>
        </section>

        <section id="cases">
          <SectionContainer>
            <h2>{t('cases')}</h2>
            <p>{t('casesSubtitle')}</p>
            <h3>{t('flightSchoolWings')}</h3><TextParagraph>{t("flightSchoolWingsDescription")}</TextParagraph>
            <ImageWrapper>
              <img src="/images/Graphite.png" alt={t('boardingPass')} width={400}/>
            </ImageWrapper>
          </SectionContainer>
        </section>

        <section id="contact">
          <SectionContainer>
            <h2>{t('contactUs')}</h2>
            <p>{t('contactUsDescription')} <a href = "mailto: eugene@simplewallet.eu">{t('email')}</a></p>
          </SectionContainer>
        </section>
      </Main>
  );
}